<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { cn } from '@/utils/utils';

/**
 * A single self-contained horizontal label/value row.
 *
 * Renders one flex `<div>` row wrapping a `<dt>` (muted, non-wrapping label on
 * the left) and a `<dd>` (value on the right side of the row). The row owns its
 * own layout, so it works standalone and composes inside an
 * {@link InspectorDetailList} (which just stacks rows) without depending on a
 * shared grid geometry.
 *
 * Provide the value via the default slot so callers can format it freely —
 * `font-mono`, `tabular-nums`, `capitalize`, `break-all`, conditional classes,
 * tooltips, etc. — or via the `value` prop for plain text. The slot wins when
 * both are given.
 *
 * By default the `<dd>` consumes the remaining width and wraps long content
 * (left-ish, not forced right-aligned) — matching the Audio File Issues /
 * Updated-Fields appearance. Pass `value-class="... text-right truncate ..."`
 * to opt into the right-aligned, truncated search look.
 *
 * `valueTitle` sets a native `title` on the `<dd>` so the full value stays
 * discoverable on hover even when the visible text is formatted. `bordered`
 * adds a per-row bottom border (`border-b last:border-b-0`) for the search
 * sections look.
 */
const props = defineProps<{
  /** Left-column label text. */
  label: string;
  /** Plain-text value; ignored when the default slot is provided. */
  value?: string;
  /** Native `title` tooltip applied to the `<dd>`. */
  valueTitle?: string;
  /** Extra classes merged onto the `<dt>` label cell. */
  labelClass?: HTMLAttributes['class'];
  /** Extra classes merged onto the `<dd>` value cell. */
  valueClass?: HTMLAttributes['class'];
  /** Adds a per-row bottom border (`border-b last:border-b-0`) for the search look. */
  bordered?: boolean;
}>();
</script>

<template>
  <div
    :class="
      cn(
        'flex items-baseline justify-between gap-4',
        props.bordered && 'border-border/40 border-b py-1 last:border-b-0'
      )
    "
  >
    <dt :class="cn('text-muted-foreground shrink-0 whitespace-nowrap', props.labelClass)">{{ label }}</dt>
    <dd :class="cn('min-w-0 break-words', props.valueClass)" :title="props.valueTitle">
      <slot>{{ props.value }}</slot>
    </dd>
  </div>
</template>
